import { useRef, useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Play, ArrowRight, ChevronLeft, ChevronRight, MapPin, MessageCircle } from "lucide-react";
import { useLang } from "@/context/LangContext";
import { useApi, waLink } from "@/lib/api";
import { useWaNumber } from "@/components/site/WhatsAppFab";

export const TIKTOK_PROFILE = "https://www.tiktok.com/@padang_taxi_guide";

const send = (frame, type) => frame?.contentWindow?.postMessage({ type, value: undefined, "x-tiktok-player": true }, "*");

export const TikTokPlayer = ({ videoId, title }) => {
  const ref = useRef(null);
  const frame = useRef(null);
  const timers = useRef([]);
  const [show, setShow] = useState(false);
  useEffect(() => {
    const io = new IntersectionObserver(([e]) => {
      if (e.isIntersecting) setShow(true);
      else { send(frame.current, "mute"); send(frame.current, "pause"); }
    }, { rootMargin: "200px" });
    io.observe(ref.current);
    const onMsg = (e) => {
      if (e.source !== frame.current?.contentWindow) return;
      const data = typeof e.data === "string" ? (() => { try { return JSON.parse(e.data); } catch { return {}; } })() : e.data;
      if (data?.type === "onPlayerReady") { timers.current.forEach(clearTimeout); send(frame.current, "mute"); }
    };
    const onHide = () => { if (document.hidden) { send(frame.current, "mute"); send(frame.current, "pause"); } };
    window.addEventListener("message", onMsg);
    document.addEventListener("visibilitychange", onHide);
    return () => {
      io.disconnect();
      window.removeEventListener("message", onMsg);
      document.removeEventListener("visibilitychange", onHide);
    };
  }, []);
  return (
    <div ref={ref} className="relative w-full" style={{ aspectRatio: "9 / 16" }}>
      {show ? (
        <iframe ref={frame} loading="lazy" data-testid="tiktok-embed" title={title || "TikTok"} src={`https://www.tiktok.com/player/v1/${videoId}?autoplay=0&music_info=1&description=1&rel=0`}
          onLoad={() => { timers.current = [300, 1500, 3000].map((ms) => setTimeout(() => send(frame.current, "mute"), ms)); }}
          className="absolute inset-0 h-full w-full" allow="autoplay; encrypted-media; fullscreen; picture-in-picture" allowFullScreen />
      ) : <div className="absolute inset-0 flex items-center justify-center"><Play className="h-10 w-10 fill-white/30 text-white/30" /></div>}
    </div>
  );
};

const VideoCard = ({ v }) => {
  const { t, lang } = useLang();
  const num = useWaNumber();
  const msg = t("wa_video").replace("{place}", v.title || "Sumatera Barat");
  const mine = lang !== "id" && v.lang === lang;
  return (
  <div data-testid={`tiktok-card-${v.video_id}`} className="w-[260px] sm:w-[300px] shrink-0 snap-start">
    <div className="relative overflow-hidden rounded-3xl bg-black shadow-xl shadow-slate-900/15 ring-1 ring-slate-200">
      <TikTokPlayer videoId={v.video_id} title={v.title} />
      {mine && <span data-testid={`tiktok-lang-badge-${v.video_id}`} className="pointer-events-none absolute left-3 top-14 rounded-full bg-[#FBBF24] px-3 py-1 text-[11px] font-extrabold uppercase tracking-wide text-slate-900 shadow">{t("video_badge")}</span>}
    </div>
    {v.title && (v.destination_slug
      ? <Link to={`/destinations/${v.destination_slug}`} data-testid={`tiktok-dest-link-${v.video_id}`} className="mt-3 flex items-center gap-1 text-sm font-bold text-slate-900 hover:text-[#E11D48]"><MapPin className="h-4 w-4 text-[#E11D48]" />{v.title}</Link>
      : <p className="mt-3 text-sm font-bold text-slate-900">{v.title}</p>)}
    <a href={num ? waLink(num, msg) : "#"} target="_blank" rel="noreferrer" data-testid={`tiktok-book-btn-${v.video_id}`}
      className="mt-2 flex items-center justify-center gap-2 rounded-full bg-[#25D366] py-2.5 text-sm font-bold text-white transition-transform hover:scale-[1.02]">
      <MessageCircle className="h-4 w-4" />{t("book_trip_here")}
    </a>
  </div>
  );
};

export const byLang = (videos, lang) => [...videos].sort((a, b) => ((b.lang || "id") === lang) - ((a.lang || "id") === lang));

export const VideoRow = ({ videos }) => {
  const { lang, sel } = useLang();
  return <div key={sel} className="no-scrollbar flex snap-x snap-mandatory gap-5 overflow-x-auto pb-2">{byLang(videos, lang).map((v) => <VideoCard key={v.id} v={v} />)}</div>;
};

export default function TikTokHook() {
  const { t, lang, sel } = useLang();
  const { data } = useApi("/videos");
  const track = useRef(null);
  const seen = new Set();
  const videos = byLang(data || [], lang).filter((v) => !v.destination_slug || (!seen.has(v.destination_slug) && seen.add(v.destination_slug)));
  if (!videos.length) return null;
  const scroll = (dir) => track.current?.scrollBy({ left: dir * 320, behavior: window.matchMedia("(prefers-reduced-motion: reduce)").matches ? "instant" : "smooth" });

  return (
    <section data-testid="tiktok-hook-section" className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-12">
      <div className="rounded-[2rem] border border-slate-200 bg-white p-6 sm:p-10">
        <div className="flex flex-wrap items-end justify-between gap-6">
          <div className="min-w-0 max-w-2xl [overflow-wrap:anywhere]">
            <p className="inline-flex max-w-full items-center gap-2 rounded-full bg-slate-900 px-4 py-1.5 text-xs font-bold uppercase tracking-wider text-white"><Play className="h-3.5 w-3.5 fill-white" />{t("hook_kicker")}</p>
            <h2 className="mt-4 text-3xl sm:text-4xl font-bold tracking-tight text-slate-900">{t("hook_title")} <span className="font-display italic font-medium text-[#E11D48]">{t("hook_title_accent")}</span></h2>
            <p className="mt-3 text-base text-slate-500">{t("hook_sub")}</p>
          </div>
          <div className="flex w-full sm:w-auto flex-wrap items-center gap-3">
            <Link to="/tours" data-testid="hook-book-tour-btn" className="inline-flex flex-1 sm:flex-none justify-center whitespace-nowrap items-center gap-2 rounded-full bg-[#E11D48] px-6 py-3 font-bold text-white transition-colors hover:bg-[#BE123C]">{t("hook_cta")}<ArrowRight className="h-4 w-4" /></Link>
            <a href={TIKTOK_PROFILE} target="_blank" rel="noreferrer" data-testid="tiktok-follow-btn" className="inline-flex flex-1 sm:flex-none justify-center whitespace-nowrap items-center gap-2 rounded-full bg-slate-900 px-5 py-3 font-bold text-white transition-colors hover:bg-slate-700">@padang_taxi_guide</a>
            {videos.length > 1 && [[-1, ChevronLeft, "prev"], [1, ChevronRight, "next"]].map(([d, Icon, k]) => (
              <button key={k} data-testid={`tiktok-${k}-btn`} onClick={() => scroll(d)} className="hidden sm:flex h-11 w-11 items-center justify-center rounded-full border border-slate-300 transition-colors hover:bg-slate-100"><Icon className="h-5 w-5" /></button>
            ))}
          </div>
        </div>
        <div key={sel} ref={track} data-testid="tiktok-gallery-track" className="no-scrollbar mt-8 flex snap-x snap-mandatory gap-5 overflow-x-auto pb-2">
          {videos.map((v) => <VideoCard key={v.id} v={v} />)}
        </div>
      </div>
    </section>
  );
}
